import { useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { api } from "../../convex/_generated/api"
import { authClient } from "@/lib/auth-client"
import { ImageUploadControl } from "@/components/image-upload-control"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { FormInput } from "@/components/form/form-field"
import { usePendingAction } from "@/hooks/use-pending-action"
import { usePrefillOnce } from "@/hooks/use-prefill-once"
import {
  imageFileValidator,
  useStorageUpload,
} from "@/hooks/use-storage-upload"
import { initials } from "@/lib/utils"
import { toast } from "sonner"

export const Route = createFileRoute("/recruit/settings/account")({
  component: EmployerAccountSettings,
})

const MAX_AVATAR_BYTES = 5 * 1024 * 1024

function EmployerAccountSettings() {
  const profile = useQuery(api.profiles.me)
  const update = useMutation(api.profiles.updateProfile)
  const setAvatar = useMutation(api.profiles.setAvatar)
  const { data: session } = authClient.useSession()
  const [name, setName] = useState("")
  const [phone, setPhone] = useState("")
  const [linkedin, setLinkedin] = useState("")
  const { pending: saving, run: runSave } = usePendingAction()

  usePrefillOnce(profile, (p) => {
    setName(p.name ?? "")
    setPhone(p.phone ?? "")
    setLinkedin(p.linkedinUrl ?? "")
  })

  const email = session?.user?.email ?? profile?.email ?? ""

  const { uploading, upload: uploadAvatar } = useStorageUpload({
    purpose: "avatar",
    validate: imageFileValidator(MAX_AVATAR_BYTES),
    onUploaded: (ref) => setAvatar(ref),
    successMessage: "Profile photo updated",
  })

  return (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault()
        if (!name.trim()) {
          toast.error("Name is required")
          return
        }
        void runSave(
          () => update({ name: name.trim(), phone, linkedinUrl: linkedin }),
          { success: "Account saved" },
        )
      }}
    >
      <div className="mkt-panel mkt-form">
        <p className="mkt-panel__title">Your account</p>
        <p className="mkt-panel__copy">
          How teammates and candidates see you as a recruiting contact.
        </p>
        <div className="mt-4 space-y-4">
          <ImageUploadControl
            label="Profile photo"
            hint="Square photos work best · 5 MB max"
            imageUrl={profile?.avatarFileUrl}
            shape="circle"
            uploading={uploading}
            onUpload={uploadAvatar}
            onRemove={async () => {
              await setAvatar({})
              toast.success("Profile photo removed")
            }}
            fallback={
              <Avatar size="lg" className="size-full rounded-full">
                <AvatarFallback className="rounded-full text-lg">
                  {initials(name || profile?.name || "?")}
                </AvatarFallback>
              </Avatar>
            }
          />
          <FormInput
            id="account-name"
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            error={!name.trim() ? "Required" : undefined}
          />
          <FormInput
            id="account-email"
            label="Email"
            value={email}
            readOnly
            disabled
          />
          <FormInput
            id="account-phone"
            label="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+91 98765 43210"
          />
          <FormInput
            id="account-linkedin"
            label="LinkedIn"
            value={linkedin}
            onChange={(e) => setLinkedin(e.target.value)}
            placeholder="https://linkedin.com/in/you"
            description="Shown with your photo on jobs you post — required before you can publish a job."
          />
        </div>
      </div>

      <button type="submit" className="home-wf-btn home-wf-btn--lime" disabled={saving}>
        {saving ? "Saving…" : "Save"}
      </button>
    </form>
  )
}
